(() => {
  const getLocale = () =>
    document.documentElement.lang || navigator.language || "de-DE";

  const formatMoneyIntl = (cents, currency) => {
  const amount = (Number(cents) || 0) / 100;

  try {
    const formatted = new Intl.NumberFormat(getLocale(), {
      style: "currency",
      currency,
    }).format(amount);

    // Remove any normal or non-breaking space before currency symbol
    return formatted.replace(/\s(?=\D+$)/, "");

  } catch {
    return `${amount.toFixed(2)}${currency}`;
  }
};

  const parseMoneyFromTextToCents = (text) => {
    if (!text) return null;

    // Remove currency symbols and spaces, keep digits + separators
    const cleaned = text.replace(/[^\d.,-]/g, "").trim();
    if (!cleaned) return null;

    // Handle formats like "1.234,56" and "1,234.56"
    // Strategy: last separator is decimal, remove others
    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");
    const decimalSep = lastComma > lastDot ? "," : ".";

    let normalized = cleaned;

    // remove thousands separators (the "other" sep)
    if (decimalSep === ",") {
      normalized = normalized.replace(/\./g, "");
      normalized = normalized.replace(",", ".");
    } else {
      normalized = normalized.replace(/,/g, "");
    }

    const value = Number(normalized);
    if (!Number.isFinite(value)) return null;

    return Math.round(value * 100);
  };

  const getCart = async () => {
    // Some themes/apps keep a cached cart object (nice if present)
    if (window.Shopify?.cart?.total_price != null) return window.Shopify.cart;

    const res = await fetch(`${Shopify.routes.root}cart.js`, { credentials: "same-origin" });
    if (!res.ok) throw new Error("cart.js failed");
    return res.json();
  };

  const findSubtotalText = (drawer) => {
    // Try common Dawn selectors (varies by version / markup)
    const selectors = [
      ".totals__subtotal-value",
      ".totals__total-value",
      "[data-cart-subtotal]",
      "[data-cart-total]",
      ".cart-drawer .totals__subtotal-value",
      ".cart-drawer__footer .totals__subtotal-value",
      ".cart-drawer__footer .totals__total-value",
    ];

    for (const sel of selectors) {
      const node = drawer.querySelector(sel);
      if (node?.textContent?.trim()) return node.textContent.trim();
      // If it’s a data attribute container
      const attr = node?.getAttribute?.("data-cart-subtotal") || node?.getAttribute?.("data-cart-total");
      if (attr && String(attr).trim()) return String(attr).trim();
    }
    return null;
  };

  const updateFromCartObject = (el, cart) => {
    const threshold = Number(el.getAttribute("data-threshold-cents") || "0");
    if (!Number.isFinite(threshold) || threshold <= 0) return;

    const total = Number(cart.total_price || 0);
    const remaining = Math.max(0, threshold - total);
    const progress = remaining > 0 ? Math.min(100, Math.round((total / threshold) * 100)) : 100;

    const textEl = el.querySelector(".free-shipping-bar__text");
    const fillEl = el.querySelector(".free-shipping-bar__fill");
    const trackEl = el.querySelector(".free-shipping-bar__track");
    if (!textEl || !fillEl || !trackEl) return;

    const currencyCode = cart.currency || "EUR";
    const textRemainingTpl =
      el.getAttribute("data-text-remaining") || "Nur noch {{ amount }} bis Gratisversand";
    const textReached =
      el.getAttribute("data-text-reached") || "Gratisversand freigeschaltet 🎉";

    if (remaining > 0) {
      const amountStr = formatMoneyIntl(remaining, currencyCode);
      textEl.textContent = textRemainingTpl.replace("{{ amount }}", amountStr);
      el.classList.remove("free-shipping-bar--unlocked");
    } else {
      textEl.textContent = textReached;
      el.classList.add("free-shipping-bar--unlocked");
    }

    fillEl.style.width = `${progress}%`;
    trackEl.setAttribute("aria-valuenow", String(progress));

    const iconEl = el.querySelector(".free-shipping-bar__icon");
    if (iconEl) iconEl.style.left = `${progress}%`;
  };

  const updateUsingDrawerSubtotal = async (drawer, el) => {
    // 1) Try instant DOM subtotal (no fetch, no flicker)
    const subtotalText = findSubtotalText(drawer);
    const subtotalCents = parseMoneyFromTextToCents(subtotalText);

    if (subtotalCents != null) {
      // We still need currency code to format remaining nicely.
      // If we can’t reliably read it from DOM, fallback to cart.js for currency only is overkill,
      // so we’ll just use active shop currency from html if present, otherwise EUR.
      const currencyGuess =
        document.documentElement.getAttribute("data-shopify-currency") ||
        document.documentElement.getAttribute("data-currency") ||
        "EUR";

      const threshold = Number(el.getAttribute("data-threshold-cents") || "0");
      if (!Number.isFinite(threshold) || threshold <= 0) return;

      const remaining = Math.max(0, threshold - subtotalCents);
      const progress = remaining > 0 ? Math.min(100, Math.round((subtotalCents / threshold) * 100)) : 100;

      const textEl = el.querySelector(".free-shipping-bar__text");
      const fillEl = el.querySelector(".free-shipping-bar__fill");
      const trackEl = el.querySelector(".free-shipping-bar__track");
      if (!textEl || !fillEl || !trackEl) return;

      const textRemainingTpl =
        el.getAttribute("data-text-remaining") || "Nur noch {{ amount }} bis Gratisversand";
      const textReached =
        el.getAttribute("data-text-reached") || "Gratisversand freigeschaltet 🎉";

      if (remaining > 0) {
        const amountStr = formatMoneyIntl(remaining, currencyGuess);
        textEl.textContent = textRemainingTpl.replace("{{ amount }}", amountStr);
        el.classList.remove("free-shipping-bar--unlocked");
      } else {
        textEl.textContent = textReached;
        el.classList.add("free-shipping-bar--unlocked");
      }

      fillEl.style.width = `${progress}%`;
      trackEl.setAttribute("aria-valuenow", String(progress));

      const iconEl = el.querySelector(".free-shipping-bar__icon");
      if (iconEl) iconEl.style.left = `${progress}%`;

      return;
    }

    // 2) Fallback: cart.js (but IMPORTANT: don’t clear UI before it arrives)
    try {
      const cart = await getCart();
      updateFromCartObject(el, cart);
    } catch {
      // If even this fails, do nothing (keep previous UI)
    }
  };

  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      const drawer = document.querySelector("cart-drawer");
      if (!drawer) return;

      const el = drawer.querySelector("[data-fsb]") || document.querySelector("[data-fsb]");
      if (!el) return;

      updateUsingDrawerSubtotal(drawer, el);
    });
  };

  const init = () => {
    // initial
    schedule();

    // Observe drawer changes (Dawn injects new content)
    const drawer = document.querySelector("cart-drawer");
    if (drawer) {
      const mo = new MutationObserver(() => schedule());
      mo.observe(drawer, { subtree: true, childList: true });
    }

    // Also react to cart endpoints (reliable)
    const origFetch = window.fetch;
    window.fetch = async (...args) => {
      const res = await origFetch(...args);
      try {
        const url = typeof args[0] === "string" ? args[0] : args[0]?.url;
        if (url && /\/cart\/(add|change|update|clear)\.js/.test(url)) schedule();
      } catch {}
      return res;
    };
  };

  document.addEventListener("DOMContentLoaded", init);
})();